import { create } from 'zustand'
import type { RackLifecycleStatus, RackRole } from '../utils/rackLifecycle.logic'

// ─── Constants ───────────────────────────────────────────────────────────────
export const CELL = 60          // px per grid cell (= 0.6 m at ~100px/m)
export const PPM  = 100         // pixels per meter

export type LensType = 'none' | 'temperature' | 'power' | 'space' | 'spof'
export type ToolType = 'select' | 'draw_tray' | 'draw_zone' | 'erase'
export type FloorEntityKind = 'rack' | 'cooling_unit' | 'power_unit' | 'architecture'
export type RackType = 'server_rack' | 'network_rack' | 'open_frame' | 'crac' | 'inrow_cooler' | 'ups' | 'pdu_frame' | 'pillar'
export type AisleType = 'hot' | 'cold' | 'containment'
export type TrayType = 'overhead' | 'underfloor' | 'ladder' | 'wire_mesh' | 'fiber_raceway' | 'power_busway'
export type TrayMedia = 'fiber' | 'copper' | 'power'
export type RackOrientation = 0 | 90 | 180 | 270
export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

// ─── Row (named group of racks) ──────────────────────────────────────────────
export interface RowConfig {
  id: string
  name: string           // user-visible name: "A", "Ряд 01", "Серверный"
  labelIndex: number     // 1-based ordering for mask {row} variable
  orientation: number    // 0 | 90 | 180 | 270 — shared orientation of all racks
  color: string          // highlight color on canvas
}

// ─── Global DC editor settings (stored in draft) ──────────────────────────────
export interface DcEditorSettings {
  namingMask: string          // e.g. "{module}/{row:02d}/{rack:02d}"
  module: string              // "1" — module/building number
  hall: string                // "A" — hall/zone letter
  defaultRackWidthM: number   // 0.6
  defaultRackDepthM: number   // 1.0
  defaultRackHeightM: number  // 2.0  (42U × 44.45mm ≈ 1.87m, standard is 2.0m)
  defaultAisleWidthM: number  // 1.2  — cold/hot aisle width
  defaultRowSpacingM: number  // 0.0  — back-to-back gap between rows
  defaultTrayHeightM: number  // 0.3  — tray height above rack top
  trunkRackIndex?: number | null // vertical trunk index
}

export const DEFAULT_DC_SETTINGS: DcEditorSettings = {
  namingMask: '{row_letter}-{rack:02d}',
  module: '1',
  hall: 'A',
  defaultRackWidthM: 0.6,
  defaultRackDepthM: 1.0,
  defaultRackHeightM: 2.0,
  defaultAisleWidthM: 1.2,
  defaultRowSpacingM: 0.0,
  defaultTrayHeightM: 0.3,
  trunkRackIndex: null,
}

// ─── Aisle Zone (hot/cold corridor, containment) ─────────────────────────────
export interface AisleZone {
  id: string
  x: number           // canvas px top-left
  y: number           // canvas px top-left
  w: number           // canvas px width
  h: number           // canvas px height
  aisleType: AisleType
  label?: string      // user label, e.g. "Холодный коридор A"
  isDirty?: boolean
  isContained?: boolean
}

export interface RackNode {
  id: string
  x: number           // canvas px
  y: number           // canvas px
  width: number       // canvas px (legacy, kept for compat)
  height: number      // canvas px (legacy, kept for compat)
  rotation: number    // degrees (legacy canvas rotation)
  orientation: RackOrientation  // physical rack orientation: 0=North(front up) | 90=East | 180=South(front down) | 270=West
  label: string
  rackType: RackType
  uCount: number
  widthM?: number     // physical width (m)
  depthM?: number     // physical depth (m)
  heightM?: number    // physical height (m), default 2.0
  tenant?: string     // 'mine' | 'other' | 'reserved'
  status?: RackLifecycleStatus // 'planned' | 'active' | 'reserved' | 'deprecated'
  role?: RackRole              // 'Server' | 'Network' | 'Storage' | 'Colocation' | 'Edge' | 'Infrastructure'
  rowId?: string      // RowConfig.id — which row this rack belongs to
  rackPosInRow?: number // 1-based position within the row
  isDirty?: boolean   // created/moved in draft, not yet published
  rowIndex: number    // DC row_index — from API or auto-assigned
  rackIndex: number   // DC rack_index — from API or auto-assigned
  isFacility?: boolean // true for CRAC, In-Row, UPS, PDU, Pillar (no U-slots)
  isBlocked?: boolean  // true for physical obstacles like Pillar
  dbId?: number       // Actual integer ID from backend RackConfig
  max_power_w?: number
  cascadeTenancy?: boolean // Whether to propagate tenant to child equipment upon commit
}

export interface TrayNode {
  id: string
  x: number
  y: number
  z_m?: number
  type: 'terminal' | 'tee'
}

export interface TrayEdge {
  id: string
  source: string      // TrayNode.id
  target: string      // TrayNode.id
  type?: 'copper' | 'fiber' | 'power' | string
  mediaType?: TrayMedia
  trayType?: TrayType // physical infrastructure type
  color: string
  widthM: number
  fillCalcEnabled?: boolean  // user opt-in: calculate cable fill capacity
  // fill calc fields (computed, not stored in DB)
  maxCables?: number         // computed from widthM × standard 40% fill
  usedCables?: number        // actual cables routed through this tray
}

// ─── History snapshot ────────────────────────────────────────────────────────
interface HistoryEntry {
  racks: Record<string, RackNode>
  trayNodes: Record<string, TrayNode>
  trayEdges: Record<string, TrayEdge>
  rows: Record<string, RowConfig>
  aisleZones: Record<string, AisleZone>
}

// ─── State ───────────────────────────────────────────────────────────────────
export interface EditorState {
  // Stage transform
  stageX: number
  stageY: number
  stageScale: number
  stageW: number
  stageH: number
  setStageTransform: (x: number, y: number, scale: number) => void
  setStageSize: (w: number, h: number) => void

  // Tool
  tool: ToolType
  setTool: (t: ToolType) => void

  // Rack objects
  racks: Record<string, RackNode>
  setRacks: (racks: Record<string, RackNode>) => void
  baselineRacks: Record<string, RackNode>
  setBaselineRacks: (racks: Record<string, RackNode>) => void
  loadRacks: (racks: Record<string, RackNode>) => void
  addRack: (rack: RackNode) => void
  addRacksBatch: (racks: RackNode[]) => void
  updateRack: (id: string, data: Partial<RackNode>) => void
  batchUpdateRacks: (ids: string[], data: Partial<RackNode>) => void
  deleteRack: (id: string) => void
  moveRacks: (moves: { id: string; x: number; y: number }[]) => void
  deleteSelected: () => void
  duplicateSelected: () => void
  selectAll: () => void

  // Rows (named groups)
  rows: Record<string, RowConfig>
  addRow: (row: RowConfig) => void
  updateRow: (id: string, data: Partial<RowConfig>) => void
  deleteRow: (id: string) => void
  loadRows: (rows: Record<string, RowConfig>) => void

  // Row & Aisle Generator
  showRowGenerator: boolean
  setShowRowGenerator: (v: boolean) => void
  addGeneratedRowBatch: (payload: { rows: RowConfig[]; racks: RackNode[]; aisleZone?: AisleZone }) => void

  // Matrix Hall Wizard
  showSetupWizard: boolean
  setShowSetupWizard: (v: boolean) => void
  applyMatrixHallBatch: (payload: { rows: Record<string, RowConfig>; racks: RackNode[]; aisleZones: Record<string, AisleZone>; replaceExisting: boolean }) => void

  // Rack Allocation Matrix
  showRackAllocation: boolean
  setShowRackAllocation: (v: boolean) => void
  applyRackAllocation: (allocations: Record<string, string>, cascadeToEquipment?: boolean) => void

  // Pathway Generator (Cable Trays)
  showPathwayGenerator: boolean
  setShowPathwayGenerator: (v: boolean) => void
  applyGeneratedPathwaysBatch: (payload: {
    nodes: Record<string, TrayNode>
    edges: Record<string, TrayEdge>
    replaceExisting: boolean
  }) => void

  // DC editor settings
  dcSettings: DcEditorSettings
  setDcSettings: (s: Partial<DcEditorSettings>) => void
  loadDcSettings: (s: Partial<DcEditorSettings>) => void

  // Aisle Zones (hot/cold corridors, containment)
  aisleZones: Record<string, AisleZone>
  addAisleZone: (zone: AisleZone) => void
  updateAisleZone: (id: string, data: Partial<AisleZone>) => void
  removeAisleZone: (id: string) => void
  loadAisleZones: (zones: Record<string, AisleZone>) => void

  // Overlay visibility toggles
  showAisles: boolean
  setShowAisles: (v: boolean) => void
  showTrays: boolean
  setShowTrays: (v: boolean) => void
  showLabels: boolean
  setShowLabels: (v: boolean) => void
  showThermal: boolean
  setShowThermal: (v: boolean) => void

  // Elevation panel visibility
  showElevation: boolean
  setShowElevation: (v: boolean) => void

  // Tray graph (cable trays)
  trayNodes: Record<string, TrayNode>
  trayEdges: Record<string, TrayEdge>
  setTrayGraph: (nodes: Record<string, TrayNode>, edges: Record<string, TrayEdge>) => void
  addTrayNode: (node: TrayNode) => void
  addTrayEdge: (edge: TrayEdge) => void

  trayDrawType: TrayMedia
  setTrayDrawType: (t: TrayMedia) => void
  updateTrayEdge: (id: string, data: Partial<TrayEdge>) => void
  deleteTrayEdge: (id: string) => void
  deleteTrayNode: (id: string) => void
  selectedEdgeId: string | null
  setSelectedEdgeId: (id: string | null) => void

  // Selection
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  toggleSelected: (id: string, additive: boolean) => void
  clearSelection: () => void

  // Hover
  hoveredId: string | null
  setHoveredId: (id: string | null) => void

  // Lens
  activeLens: LensType
  setActiveLens: (lens: LensType) => void

  // Telemetry
  telemetryData: Record<string, any>
  setTelemetryData: (data: Record<string, any>) => void

  // What-If Simulation
  whatIfActive: boolean
  setWhatIfActive: (active: boolean) => void
  whatIfData: Record<string, any>
  setWhatIfData: (data: Record<string, any>) => void

  // Save status (shown in StatusBar)
  saveStatus: SaveStatus
  setSaveStatus: (s: SaveStatus) => void

  // History (Undo / Redo)
  _history: HistoryEntry[]
  _historyIdx: number
  pushHistory: () => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean

  // Palette rack sequence counter (starts at 10000, increments per palette-dropped rack)
  // Ensures unique row/rack indices that don't collide with real DB racks (0–~50 range)
  _paletteSeq: number

  // Draw-tray state (ephemeral)
  drawingTrayFromId: string | null
  setDrawingTrayFromId: (id: string | null) => void

  // Marquee selection state (ephemeral)
  marquee: { x: number; y: number; w: number; h: number } | null
  setMarquee: (m: { x: number; y: number; w: number; h: number } | null) => void

  // Collisions
  collidingIds: string[]
  setCollidingIds: (ids: string[]) => void

  // Clipboard
  _clipboard: RackNode[]
  copySelected: () => void
  pasteClipboard: () => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function snapshot(s: EditorState): HistoryEntry {
  return {
    racks: structuredClone(s.racks),
    trayNodes: structuredClone(s.trayNodes),
    trayEdges: structuredClone(s.trayEdges),
    rows: structuredClone(s.rows),
    aisleZones: structuredClone(s.aisleZones),
  }
}

function pushSnap(s: EditorState, snap: HistoryEntry) {
  const base = s._history.slice(0, s._historyIdx + 1)
  const next = [...base, snap].slice(-MAX_HISTORY)
  const nextIdx = next.length - 1
  return { _history: next, _historyIdx: nextIdx, canUndo: nextIdx > 0, canRedo: false }
}

function pushNewState(s: EditorState, updates: Partial<EditorState>) {
  const nextState = { ...s, ...updates } as EditorState
  const snap = snapshot(nextState)
  return { ...updates, ...pushSnap(s, snap) }
}

const MAX_HISTORY = 50

// ─── Store ───────────────────────────────────────────────────────────────────
export const useEditorStore = create<EditorState>((set, get) => ({
  // Stage
  stageX: 0, stageY: 0, stageScale: 1, stageW: 800, stageH: 600,
  setStageTransform: (x, y, scale) => set({ stageX: x, stageY: y, stageScale: scale }),
  setStageSize: (w, h) => set({ stageW: w, stageH: h }),

  // Tool
  tool: 'select',
  setTool: (t) => set({ tool: t }),

  // Tray Draw Type
  trayDrawType: 'fiber',
  setTrayDrawType: (t) => set({ trayDrawType: t }),

  // Racks
  racks: {},
  setRacks: (racks) => set({ racks }),

  // Baseline for calculating staged changes
  baselineRacks: {},
  setBaselineRacks: (racks) => set({ baselineRacks: racks }),

  // Load racks from API + reset history baseline so Undo doesn't go to empty state
  loadRacks: (racks) => set(s => ({
    racks,
    _history: [{
      racks: structuredClone(racks),
      trayNodes: structuredClone(s.trayNodes),
      trayEdges: structuredClone(s.trayEdges),
      rows: structuredClone(s.rows),
      aisleZones: structuredClone(s.aisleZones),
    }],
    _historyIdx: 0,
    canUndo: false,
    canRedo: false,
    saveStatus: 'idle' as SaveStatus,
  })),

  // Add rack — atomic: push history + add rack + increment palette seq in ONE set()
  addRacksBatch: (newRacks) => set(s => {
    const racks = { ...s.racks }
    newRacks.forEach(r => { racks[r.id] = r })
    return pushNewState(s, {
      racks,
      _paletteSeq: s._paletteSeq + newRacks.length,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  addRack: (rack) => set(s => pushNewState(s, {
    racks: { ...s.racks, [rack.id]: rack },
    // Increment palette sequence so next palette rack gets a unique rackIndex
    _paletteSeq: s._paletteSeq + 1,
    saveStatus: 'dirty' as SaveStatus,
  })),

  updateRack: (id, data) => set(s => {
    const current = s.racks[id]
    if (!current) return {}
    const updatedRack = { ...current, ...data }
    if (data.widthM !== undefined) {
      updatedRack.width = Math.round(data.widthM * PPM)
    }
    if (data.depthM !== undefined) {
      updatedRack.height = Math.round(data.depthM * PPM)
    }
    // Auto-recalc uCount from heightM (1U = 44.45mm) — only for IT racks, not when uCount is explicitly set
    if (data.heightM !== undefined && data.uCount === undefined && !updatedRack.isFacility) {
      updatedRack.uCount = Math.round(data.heightM * 1000 / 44.45)
    }
    const trayNodes = { ...s.trayNodes }
    if (trayNodes[id]) {
      const rw = (updatedRack.widthM || 0.6) * 100
      const rh = (updatedRack.depthM || 1.0) * 100
      trayNodes[id] = {
        ...trayNodes[id],
        x: updatedRack.x + rw / 2,
        y: updatedRack.y + rh / 2,
      }
    }
    return pushNewState(s, {
      racks: { ...s.racks, [id]: updatedRack },
      trayNodes,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Batch update multiple racks atomically (single undo step)
  batchUpdateRacks: (ids: string[], data: Partial<RackNode>) => set(s => {
    const racks = { ...s.racks }
    ids.forEach(id => {
      const r = racks[id]
      if (!r) return
      racks[id] = { ...r, ...data, isDirty: true }
    })
    return pushNewState(s, { racks, saveStatus: 'dirty' as SaveStatus })
  }),

  // Delete rack — atomic: push history + delete in ONE set()
  deleteRack: (id) => set(s => {
    const racks = { ...s.racks }
    delete racks[id]
    // Проблема 15: каскадно удаляем TrayNode и все подключённые к нему рёбра
    const trayNodes = { ...s.trayNodes }
    const trayEdges = { ...s.trayEdges }
    if (trayNodes[id]) {
      delete trayNodes[id]
      Object.keys(trayEdges).forEach(eid => {
        const e = trayEdges[eid]
        if (e.source === id || e.target === id) delete trayEdges[eid]
      })
    }
    return pushNewState(s, {
      racks,
      trayNodes,
      trayEdges,
      selectedIds: s.selectedIds.filter(sid => sid !== id),
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Move multiple racks atomically (history + positions in ONE set)
  moveRacks: (moves) => set(s => {
    const newRacks = { ...s.racks }
    const newTrayNodes = { ...s.trayNodes }
    moves.forEach(({ id, x, y }) => {
      if (!newRacks[id]) return
      newRacks[id] = { ...newRacks[id], x, y, isDirty: true }
      // Проблема 15: синхронно двигаем TrayNode (если он привязан к этой стойке по id)
      if (newTrayNodes[id]) {
        const rack = newRacks[id]
        newTrayNodes[id] = {
          ...newTrayNodes[id],
          x: x + ((rack.widthM || 0.6) * 100) / 2,
          y: y + ((rack.depthM || 1.0) * 100) / 2,
        }
      }
    })
    return pushNewState(s, {
      racks: newRacks,
      trayNodes: newTrayNodes,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Delete all selected racks, aisle zones and selected tray edge atomically
  deleteSelected: () => set(s => {
    if (!s.selectedIds.length && !s.selectedEdgeId) return {}
    const racks = { ...s.racks }
    const aisleZones = { ...s.aisleZones }
    const trayNodes = { ...s.trayNodes }
    const trayEdges = { ...s.trayEdges }
    let selectedEdgeId = s.selectedEdgeId

    if (s.selectedEdgeId && trayEdges[s.selectedEdgeId]) {
      delete trayEdges[s.selectedEdgeId]
      selectedEdgeId = null
    }

    s.selectedIds.forEach(id => {
      if (racks[id]) {
        delete racks[id]
        // Проблема 15: каскадное удаление TrayNode и связанных рёбер
        if (trayNodes[id]) {
          delete trayNodes[id]
          Object.keys(trayEdges).forEach(eid => {
            const e = trayEdges[eid]
            if (e.source === id || e.target === id) delete trayEdges[eid]
          })
        }
      }
      if (aisleZones[id]) {
        delete aisleZones[id]
      }
    })
    return pushNewState(s, {
      racks,
      aisleZones,
      trayNodes,
      trayEdges,
      selectedIds: [],
      selectedEdgeId,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Duplicate all selected racks atomically (offset +1 cell)
  // Each duplicate gets a unique palette-range rackIndex so it's always INSERTed on publish
  duplicateSelected: () => set(s => {
    if (!s.selectedIds.length) return {}
    const newRacks = { ...s.racks }
    const newIds: string[] = []
    let seq = s._paletteSeq
    s.selectedIds.forEach(id => {
      const r = s.racks[id]
      if (!r) return
      const { dbId: _dbId, ...rackData } = r
      const newId = `dup-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      newRacks[newId] = {
        ...rackData,
        id: newId,
        x: r.x + CELL,
        y: r.y + CELL,
        label: r.label ? `${r.label} (копия)` : r.label,
        isDirty: true,
        // Unique palette indices — won't collide with original or other duplicates
        rowIndex: 10000,
        rackIndex: seq,
      }
      newIds.push(newId)
      seq++
    })
    return pushNewState(s, {
      racks: newRacks,
      selectedIds: newIds,
      _paletteSeq: seq,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Clipboard
  _clipboard: [],
  copySelected: () => set(s => {
    const copied = s.selectedIds.map(id => s.racks[id]).filter(Boolean)
    return { _clipboard: structuredClone(copied) }
  }),
  pasteClipboard: () => set(s => {
    if (!s._clipboard.length) return {}
    const suffix = window.prompt('Суффикс для имён вставленных объектов:', ' (копия)') ?? ' (копия)'
    const newRacks = { ...s.racks }
    const newIds: string[] = []
    let seq = s._paletteSeq
    s._clipboard.forEach(r => {
      const newId = `paste-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      const { dbId: _dbId, ...rackData } = r
      newRacks[newId] = {
        ...rackData,
        id: newId,
        x: r.x + CELL,
        y: r.y + CELL,
        label: r.label ? `${r.label}${suffix}` : r.label,
        isDirty: true,
        rowIndex: 10000,
        rackIndex: seq,
      }
      newIds.push(newId)
      seq++
    })
    return pushNewState(s, {
      racks: newRacks,
      selectedIds: newIds,
      _paletteSeq: seq,
      saveStatus: 'dirty' as SaveStatus,
    })
  }),

  // Select all racks
  selectAll: () => set(s => ({ selectedIds: Object.keys(s.racks) })),

  // Aisle Zones
  aisleZones: {},
  addAisleZone: (zone) => set(s => pushNewState(s, {
    aisleZones: { ...s.aisleZones, [zone.id]: zone },
    saveStatus: 'dirty' as SaveStatus,
  })),
  updateAisleZone: (id, data) => set(s => pushNewState(s, {
    aisleZones: { ...s.aisleZones, [id]: { ...s.aisleZones[id], ...data } },
    saveStatus: 'dirty' as SaveStatus,
  })),
  removeAisleZone: (id) => set(s => {
    const aisleZones = { ...s.aisleZones }
    delete aisleZones[id]
    return pushNewState(s, { aisleZones, saveStatus: 'dirty' as SaveStatus })
  }),
  loadAisleZones: (zones) => set(s => {
    const newHistory = [...s._history]
    if (newHistory.length > 0 && s._historyIdx === 0) {
      newHistory[0] = {
        ...newHistory[0],
        aisleZones: structuredClone(zones),
      }
    }
    return { aisleZones: zones, _history: newHistory }
  }),

  // Overlay toggles
  showAisles: true,
  setShowAisles: (v) => set({ showAisles: v }),
  showTrays: true,
  setShowTrays: (v) => set({ showTrays: v }),
  showLabels: true,
  setShowLabels: (v) => set({ showLabels: v }),
  showThermal: false,
  setShowThermal: (v) => set({ showThermal: v }),

  // Tray graph
  trayNodes: {}, trayEdges: {},
  setTrayGraph: (nodes, edges) => set(s => {
    const newHistory = [...s._history]
    if (newHistory.length > 0 && s._historyIdx === 0) {
      newHistory[0] = {
        ...newHistory[0],
        trayNodes: JSON.parse(JSON.stringify(nodes)),
        trayEdges: JSON.parse(JSON.stringify(edges)),
      }
    }
    return { trayNodes: nodes, trayEdges: edges, _history: newHistory }
  }),
  // Mark dirty so auto-save triggers when trays are drawn
  addTrayNode: (node) => set(s => pushNewState(s, { trayNodes: { ...s.trayNodes, [node.id]: node }, saveStatus: 'dirty' as SaveStatus })),
  addTrayEdge: (edge) => set(s => {
    if (edge.source === edge.target) return s
    const isDup = Object.values(s.trayEdges).some(
      e => (e.source === edge.source && e.target === edge.target) ||
           (e.source === edge.target && e.target === edge.source)
    )
    if (isDup) return s
    return pushNewState(s, { trayEdges: { ...s.trayEdges, [edge.id]: edge }, saveStatus: 'dirty' as SaveStatus })
  }),
  updateTrayEdge: (id, data) => set(s => {
    if (!s.trayEdges[id]) return s;
    return pushNewState(s, {
      trayEdges: { ...s.trayEdges, [id]: { ...s.trayEdges[id], ...data } },
      saveStatus: 'dirty' as SaveStatus,
    });
  }),
  deleteTrayEdge: (id) => set(s => {
    const trayEdges = { ...s.trayEdges };
    delete trayEdges[id];
    return pushNewState(s, {
      trayEdges,
      selectedEdgeId: s.selectedEdgeId === id ? null : s.selectedEdgeId,
      saveStatus: 'dirty' as SaveStatus,
    });
  }),
  deleteTrayNode: (id) => set(s => {
    const trayNodes = { ...s.trayNodes };
    const trayEdges = { ...s.trayEdges };
    delete trayNodes[id];
    Object.keys(trayEdges).forEach(eid => {
      const e = trayEdges[eid];
      if (e.source === id || e.target === id) {
        delete trayEdges[eid];
      }
    });
    return pushNewState(s, {
      trayNodes,
      trayEdges,
      drawingTrayFromId: s.drawingTrayFromId === id ? null : s.drawingTrayFromId,
      saveStatus: 'dirty' as SaveStatus,
    });
  }),
  selectedEdgeId: null,
  setSelectedEdgeId: (id) => set(s => ({ selectedEdgeId: id, selectedIds: id ? [] : s.selectedIds })),

  // Selection
  selectedIds: [],
  setSelectedIds: (ids) => set(s => ({ selectedIds: ids, selectedEdgeId: ids.length > 0 ? null : s.selectedEdgeId })),
  toggleSelected: (id, additive) => set(s => {
    if (additive) {
      const already = s.selectedIds.includes(id)
      return {
        selectedIds: already ? s.selectedIds.filter(x => x !== id) : [...s.selectedIds, id],
        selectedEdgeId: null,
      }
    }
    return { selectedIds: [id], selectedEdgeId: null }
  }),
  clearSelection: () => set({ selectedIds: [], selectedEdgeId: null }),

  // Hover
  hoveredId: null,
  setHoveredId: (id) => set({ hoveredId: id }),

  // Lens
  activeLens: 'none',
  setActiveLens: (lens) => set({ activeLens: lens }),

  // Telemetry
  telemetryData: {},
  setTelemetryData: (data) => set({ telemetryData: data }),

  // What-If Simulation
  whatIfActive: false,
  setWhatIfActive: (active) => set({ whatIfActive: active }),
  whatIfData: {},
  setWhatIfData: (data) => set({ whatIfData: data }),

  // Save status
  saveStatus: 'idle',
  setSaveStatus: (s) => set({ saveStatus: s }),

  // History — seed with an empty snapshot so the very first action is undoable
  _history: [{ racks: {}, trayNodes: {}, trayEdges: {}, rows: {}, aisleZones: {} }],
  _historyIdx: 0,
  canUndo: false,
  canRedo: false,
  // Palette rack counter — starts high to avoid collision with real DB indices (0–~50)
  _paletteSeq: 10000,
  pushHistory: () => set(s => {
    const snap = snapshot(s)
    return pushSnap(s, snap)
  }),
  undo: () => set(s => {
    const idx = s._historyIdx - 1
    if (idx < 0) return {}
    const entry = s._history[idx]
    const safeSelectedEdgeId = (s.selectedEdgeId && entry.trayEdges[s.selectedEdgeId]) ? s.selectedEdgeId : null
    const safeSelectedIds = s.selectedIds.filter(id => Boolean(entry.racks[id]))
    // After undo, state is different from server — mark dirty so auto-save fires
    return {
      ...entry,
      selectedEdgeId: safeSelectedEdgeId,
      selectedIds: safeSelectedIds,
      drawingTrayFromId: null,
      _historyIdx: idx,
      canUndo: idx > 0,
      canRedo: true,
      saveStatus: 'dirty' as SaveStatus
    }
  }),
  redo: () => set(s => {
    const idx = s._historyIdx + 1
    if (idx >= s._history.length) return {}
    const entry = s._history[idx]
    const safeSelectedEdgeId = (s.selectedEdgeId && entry.trayEdges[s.selectedEdgeId]) ? s.selectedEdgeId : null
    const safeSelectedIds = s.selectedIds.filter(id => Boolean(entry.racks[id]))
    // After redo, state is different from server — mark dirty so auto-save fires
    return {
      ...entry,
      selectedEdgeId: safeSelectedEdgeId,
      selectedIds: safeSelectedIds,
      drawingTrayFromId: null,
      _historyIdx: idx,
      canUndo: idx > 0,
      canRedo: idx < s._history.length - 1,
      saveStatus: 'dirty' as SaveStatus
    }
  }),

  // Draw-tray ephemeral
  drawingTrayFromId: null,
  setDrawingTrayFromId: (id) => set({ drawingTrayFromId: id }),

  // Marquee
  marquee: null,
  setMarquee: (m) => set({ marquee: m }),

  // Rows (named groups of racks)
  rows: {},
  addRow: (row) => set(s => pushNewState(s, { rows: { ...s.rows, [row.id]: row }, saveStatus: 'dirty' as SaveStatus })),
  updateRow: (id, data) => set(s => pushNewState(s, { rows: { ...s.rows, [id]: { ...s.rows[id], ...data } }, saveStatus: 'dirty' as SaveStatus })),
  deleteRow: (id) => set(s => {
    const rows = { ...s.rows }
    delete rows[id]
    // Detach racks from this row
    const racks = { ...s.racks }
    Object.keys(racks).forEach(rid => {
      if (racks[rid].rowId === id) racks[rid] = { ...racks[rid], rowId: undefined, rackPosInRow: undefined }
    })
    return pushNewState(s, { rows, racks, saveStatus: 'dirty' as SaveStatus })
  }),
  loadRows: (rows) => set(s => {
    const newHistory = [...s._history]
    if (newHistory.length > 0 && s._historyIdx === 0) {
      newHistory[0] = {
        ...newHistory[0],
        rows: structuredClone(rows),
      }
    }
    return { rows, _history: newHistory }
  }),

  // DC editor settings
  dcSettings: { ...DEFAULT_DC_SETTINGS },
  setDcSettings: (s) => set(state => ({
    dcSettings: { ...state.dcSettings, ...s },
    saveStatus: 'dirty' as SaveStatus,
  })),
  loadDcSettings: (s) => set(state => ({
    dcSettings: { ...state.dcSettings, ...s },
  })),

  // Elevation panel
  showElevation: false,
  setShowElevation: (v) => set({ showElevation: v }),

  // Row & Aisle Generator
  showRowGenerator: false,
  setShowRowGenerator: (v) => set({ showRowGenerator: v }),
  addGeneratedRowBatch: (payload) => set(s => {
    const nextRows = { ...s.rows }
    payload.rows.forEach(r => { nextRows[r.id] = r })

    const nextRacks = { ...s.racks }
    payload.racks.forEach(r => { nextRacks[r.id] = r })

    const nextAisles = { ...s.aisleZones }
    if (payload.aisleZone) {
      nextAisles[payload.aisleZone.id] = payload.aisleZone
    }

    return pushNewState(s, {
      rows: nextRows,
      racks: nextRacks,
      aisleZones: nextAisles,
      _paletteSeq: s._paletteSeq + payload.racks.length,
      saveStatus: 'dirty' as SaveStatus,
      showRowGenerator: false,
    })
  }),

  // Matrix Hall Wizard
  showSetupWizard: false,
  setShowSetupWizard: (v) => set({ showSetupWizard: v }),
  applyMatrixHallBatch: ({ rows, racks, aisleZones, replaceExisting }) => set(s => {
    const nextRows = replaceExisting ? { ...rows } : { ...s.rows, ...rows }
    const nextRacks = replaceExisting ? {} : { ...s.racks }
    racks.forEach(r => { nextRacks[r.id] = r })
    const nextAisles = replaceExisting ? { ...aisleZones } : { ...s.aisleZones, ...aisleZones }
    const nextTrayNodes = replaceExisting ? {} : s.trayNodes
    const nextTrayEdges = replaceExisting ? {} : s.trayEdges

    return pushNewState(s, {
      rows: nextRows,
      racks: nextRacks,
      aisleZones: nextAisles,
      trayNodes: nextTrayNodes,
      trayEdges: nextTrayEdges,
      selectedIds: [],
      _paletteSeq: s._paletteSeq + racks.length,
      saveStatus: 'dirty' as SaveStatus,
      showSetupWizard: false,
    })
  }),

  // Rack Allocation Matrix
  showRackAllocation: false,
  setShowRackAllocation: (v) => set({ showRackAllocation: v }),
  applyRackAllocation: (allocations, cascadeToEquipment = true) => set(s => {
    const racks = { ...s.racks }
    let changed = false
    Object.entries(allocations).forEach(([id, tenant]) => {
      const r = racks[id]
      if (r && (r.tenant || 'mine') !== tenant) {
        racks[id] = { ...r, tenant, isDirty: true, cascadeTenancy: cascadeToEquipment }
        changed = true
      }
    })
    if (!changed) return { showRackAllocation: false }
    return pushNewState(s, {
      racks,
      saveStatus: 'dirty' as SaveStatus,
      showRackAllocation: false,
    })
  }),

  // Pathway Generator (Cable Trays)
  showPathwayGenerator: false,
  setShowPathwayGenerator: (v) => set({ showPathwayGenerator: v }),
  applyGeneratedPathwaysBatch: ({ nodes, edges, replaceExisting }) => set(s => {
    const safeNodes = nodes || {}
    const safeEdges = edges || {}
    const nextTrayNodes = replaceExisting ? { ...safeNodes } : { ...s.trayNodes, ...safeNodes }

    let nextTrayEdges: Record<string, TrayEdge> = replaceExisting ? { ...safeEdges } : { ...s.trayEdges }
    if (!replaceExisting && safeEdges) {
      const existingPairs = new Set<string>()
      for (const e of Object.values(s.trayEdges)) {
        existingPairs.add(`${e.source}__${e.target}`)
        existingPairs.add(`${e.target}__${e.source}`)
      }
      for (const [eId, edge] of Object.entries(safeEdges)) {
        const pairKey = `${edge.source}__${edge.target}`
        if (!existingPairs.has(pairKey)) {
          nextTrayEdges[eId] = edge
          existingPairs.add(pairKey)
          existingPairs.add(`${edge.target}__${edge.source}`)
        }
      }
    }

    const nextSelectedEdgeId = (s.selectedEdgeId && nextTrayEdges[s.selectedEdgeId]) ? s.selectedEdgeId : null

    return pushNewState(s, {
      trayNodes: nextTrayNodes,
      trayEdges: nextTrayEdges,
      selectedEdgeId: nextSelectedEdgeId,
      drawingTrayFromId: null,
      showTrays: true,
      saveStatus: 'dirty' as SaveStatus,
      showPathwayGenerator: false,
    })
  }),

  // Collisions
  collidingIds: [],
  setCollidingIds: (ids) => set({ collidingIds: ids }),

  // Legacy compat shim
  get selectedId() { return get().selectedIds[0] ?? null },
}))
